/**
 * Dynamic placeholder questions for the oracle input.
 * Organized by locale and displayed randomly.
 */

/**
 * Oracle Card Placeholder Questions by Spread Type
 * Each spread type has questions tailored to its purpose
 */

// Single Card: Daily guidance, quick clarity, decision validation
export const SINGLE_CARD_PLACEHOLDER_QUESTIONS = {
  en: [
    { emoji: '🌅', text: 'What energy should I embrace today?' },
    { emoji: '🔮', text: 'What do I need to know right now?' },
    { emoji: '✨', text: 'What guidance does the universe have for me today?' },
    { emoji: '🌱', text: 'What is blocking my progress?' },
    { emoji: '💫', text: 'What am I being called to release?' },
    { emoji: '🌟', text: 'What opportunity should I focus on?' },
    { emoji: '🦋', text: 'What transformation is happening?' },
    { emoji: '⚡', text: 'What action should I take next?' },
    { emoji: '🎯', text: 'What is my next step?' },
    { emoji: '💎', text: 'What truth am I ready to face?' }
  ],
  es: [
    { emoji: '🌅', text: '¿Qué energía debo abrazar hoy?' },
    { emoji: '🔮', text: '¿Qué necesito saber ahora mismo?' },
    { emoji: '✨', text: '¿Qué guía tiene el universo para mí hoy?' },
    { emoji: '🌱', text: '¿Qué está bloqueando mi progreso?' },
    { emoji: '💫', text: '¿Qué me está pidiendo que libere?' },
    { emoji: '🌟', text: '¿En qué oportunidad debo enfocarme?' },
    { emoji: '🦋', text: '¿Qué transformación está ocurriendo?' },
    { emoji: '⚡', text: '¿Qué acción debo tomar a continuación?' },
    { emoji: '🎯', text: '¿Cuál es mi próximo paso?' },
    { emoji: '💎', text: '¿Qué verdad estoy listo para enfrentar?' }
  ],
  it: [
    { emoji: '🌅', text: 'Quale energia dovrei abbracciare oggi?' },
    { emoji: '🔮', text: 'Cosa devo sapere adesso?' },
    { emoji: '✨', text: 'Quale guida ha l\'universo per me oggi?' },
    { emoji: '🌱', text: 'Cosa sta bloccando il mio progresso?' },
    { emoji: '💫', text: 'Cosa mi viene chiesto di rilasciare?' },
    { emoji: '🌟', text: 'Su quale opportunità dovrei concentrarmi?' },
    { emoji: '🦋', text: 'Quale trasformazione sta avvenendo?' },
    { emoji: '⚡', text: 'Quale azione dovrei intraprendere dopo?' },
    { emoji: '🎯', text: 'Qual è il mio prossimo passo?' },
    { emoji: '💎', text: 'Quale verità sono pronto ad affrontare?' }
  ]
};

// Past-Present-Future: Timeline understanding, journey through time
export const PAST_PRESENT_FUTURE_PLACEHOLDER_QUESTIONS = {
  en: [
    { emoji: '🕰️', text: 'How did I get here and where am I going?' },
    { emoji: '🌊', text: 'What is the trajectory of my current situation?' },
    { emoji: '🔄', text: 'Show me my journey regarding this challenge' },
    { emoji: '📜', text: 'What is unfolding in my life right now?' },
    { emoji: '🌅', text: 'How has my past led to this present moment?' },
    { emoji: '🔮', text: 'What does my future hold based on my current path?' },
    { emoji: '✨', text: 'What patterns am I repeating from the past?' },
    { emoji: '🌟', text: 'How can I change my future trajectory?' },
    { emoji: '🌱', text: 'What seeds did I plant that are now growing?' },
    { emoji: '💫', text: 'What is the timeline of this situation?' }
  ],
  es: [
    { emoji: '🕰️', text: '¿Cómo llegué aquí y hacia dónde voy?' },
    { emoji: '🌊', text: '¿Cuál es la trayectoria de mi situación actual?' },
    { emoji: '🔄', text: 'Muéstrame mi viaje respecto a este desafío' },
    { emoji: '📜', text: '¿Qué se está desarrollando en mi vida ahora mismo?' },
    { emoji: '🌅', text: '¿Cómo mi pasado me llevó a este momento presente?' },
    { emoji: '🔮', text: '¿Qué me depara el futuro basado en mi camino actual?' },
    { emoji: '✨', text: '¿Qué patrones estoy repitiendo del pasado?' },
    { emoji: '🌟', text: '¿Cómo puedo cambiar mi trayectoria futura?' },
    { emoji: '🌱', text: '¿Qué semillas planté que ahora están creciendo?' },
    { emoji: '💫', text: '¿Cuál es la línea de tiempo de esta situación?' }
  ],
  it: [
    { emoji: '🕰️', text: 'Come sono arrivato qui e dove sto andando?' },
    { emoji: '🌊', text: 'Qual è la traiettoria della mia situazione attuale?' },
    { emoji: '🔄', text: 'Mostrami il mio viaggio riguardo a questa sfida' },
    { emoji: '📜', text: 'Cosa si sta sviluppando nella mia vita adesso?' },
    { emoji: '🌅', text: 'Come il mio passato mi ha portato a questo momento presente?' },
    { emoji: '🔮', text: 'Cosa mi riserva il futuro basato sul mio percorso attuale?' },
    { emoji: '✨', text: 'Quali modelli sto ripetendo dal passato?' },
    { emoji: '🌟', text: 'Come posso cambiare la mia traiettoria futura?' },
    { emoji: '🌱', text: 'Quali semi ho piantato che ora stanno crescendo?' },
    { emoji: '💫', text: 'Qual è la linea temporale di questa situazione?' }
  ]
};

// Situation-Action-Outcome: Decision-making, problem-solving, action planning
export const SITUATION_ACTION_OUTCOME_PLACEHOLDER_QUESTIONS = {
  en: [
    { emoji: '🎯', text: 'What should I do about this situation?' },
    { emoji: '⚡', text: 'How can I improve this area of my life?' },
    { emoji: '🔑', text: 'What is the best approach to this challenge?' },
    { emoji: '💡', text: 'I\'m facing this problem - what\'s my best move?' },
    { emoji: '🌊', text: 'What action will lead to the best outcome?' },
    { emoji: '🔥', text: 'How should I handle this decision?' },
    { emoji: '✨', text: 'What steps should I take to resolve this?' },
    { emoji: '🌟', text: 'What is the right action for this situation?' },
    { emoji: '🦋', text: 'How can I transform this challenge into opportunity?' },
    { emoji: '🌿', text: 'What should I do to move forward?' }
  ],
  es: [
    { emoji: '🎯', text: '¿Qué debo hacer sobre esta situación?' },
    { emoji: '⚡', text: '¿Cómo puedo mejorar esta área de mi vida?' },
    { emoji: '🔑', text: '¿Cuál es el mejor enfoque para este desafío?' },
    { emoji: '💡', text: 'Estoy enfrentando este problema - ¿cuál es mi mejor movimiento?' },
    { emoji: '🌊', text: '¿Qué acción llevará al mejor resultado?' },
    { emoji: '🔥', text: '¿Cómo debo manejar esta decisión?' },
    { emoji: '✨', text: '¿Qué pasos debo tomar para resolver esto?' },
    { emoji: '🌟', text: '¿Cuál es la acción correcta para esta situación?' },
    { emoji: '🦋', text: '¿Cómo puedo transformar este desafío en oportunidad?' },
    { emoji: '🌿', text: '¿Qué debo hacer para avanzar?' }
  ],
  it: [
    { emoji: '🎯', text: 'Cosa dovrei fare riguardo a questa situazione?' },
    { emoji: '⚡', text: 'Come posso migliorare quest\'area della mia vita?' },
    { emoji: '🔑', text: 'Qual è il miglior approccio a questa sfida?' },
    { emoji: '💡', text: 'Sto affrontando questo problema - qual è la mia mossa migliore?' },
    { emoji: '🌊', text: 'Quale azione porterà al miglior risultato?' },
    { emoji: '🔥', text: 'Come dovrei gestire questa decisione?' },
    { emoji: '✨', text: 'Quali passi dovrei fare per risolvere questo?' },
    { emoji: '🌟', text: 'Qual è l\'azione giusta per questa situazione?' },
    { emoji: '🦋', text: 'Come posso trasformare questa sfida in opportunità?' },
    { emoji: '🌿', text: 'Cosa dovrei fare per andare avanti?' }
  ]
};

// 5-Card Cross: Complex situations, major decisions, comprehensive analysis
export const FIVE_CARD_CROSS_PLACEHOLDER_QUESTIONS = {
  en: [
    { emoji: '🔮', text: 'Show me the full picture of this situation' },
    { emoji: '💎', text: 'What do I need to know about this major decision?' },
    { emoji: '🌌', text: 'Guide me through this complex challenge' },
    { emoji: '🌟', text: 'Help me understand all aspects of this situation' },
    { emoji: '🌊', text: 'What is the complete truth about this matter?' },
    { emoji: '🦋', text: 'Show me all dimensions of this life change' },
    { emoji: '✨', text: 'What is the deeper meaning behind this situation?' },
    { emoji: '🌿', text: 'Help me see the whole picture clearly' },
    { emoji: '🔥', text: 'What are all the factors I need to consider?' },
    { emoji: '🌈', text: 'Guide me through this month\'s journey' }
  ],
  es: [
    { emoji: '🔮', text: 'Muéstrame el panorama completo de esta situación' },
    { emoji: '💎', text: '¿Qué necesito saber sobre esta decisión importante?' },
    { emoji: '🌌', text: 'Guíame a través de este desafío complejo' },
    { emoji: '🌟', text: 'Ayúdame a entender todos los aspectos de esta situación' },
    { emoji: '🌊', text: '¿Cuál es la verdad completa sobre este asunto?' },
    { emoji: '🦋', text: 'Muéstrame todas las dimensiones de este cambio de vida' },
    { emoji: '✨', text: '¿Cuál es el significado más profundo detrás de esta situación?' },
    { emoji: '🌿', text: 'Ayúdame a ver el panorama completo claramente' },
    { emoji: '🔥', text: '¿Cuáles son todos los factores que necesito considerar?' },
    { emoji: '🌈', text: 'Guíame a través del viaje de este mes' }
  ],
  it: [
    { emoji: '🔮', text: 'Mostrami il quadro completo di questa situazione' },
    { emoji: '💎', text: 'Cosa devo sapere su questa decisione importante?' },
    { emoji: '🌌', text: 'Guidami attraverso questa sfida complessa' },
    { emoji: '🌟', text: 'Aiutami a capire tutti gli aspetti di questa situazione' },
    { emoji: '🌊', text: 'Qual è la verità completa su questa questione?' },
    { emoji: '🦋', text: 'Mostrami tutte le dimensioni di questo cambiamento di vita' },
    { emoji: '✨', text: 'Qual è il significato più profondo dietro questa situazione?' },
    { emoji: '🌿', text: 'Aiutami a vedere il quadro completo chiaramente' },
    { emoji: '🔥', text: 'Quali sono tutti i fattori che devo considerare?' },
    { emoji: '🌈', text: 'Guidami attraverso il viaggio di questo mese' }
  ]
};

// Relationship Spread: Relationship dynamics, understanding connections
export const RELATIONSHIP_SPREAD_PLACEHOLDER_QUESTIONS = {
  en: [
    { emoji: '💕', text: 'What\'s the dynamic between me and this person?' },
    { emoji: '❤️', text: 'Help me understand my relationship with them' },
    { emoji: '🤝', text: 'What does this person need from me?' },
    { emoji: '🌹', text: 'What is the lesson in this relationship?' },
    { emoji: '💫', text: 'Where is this relationship heading?' },
    { emoji: '🔮', text: 'What is the true nature of our connection?' },
    { emoji: '✨', text: 'How can we improve our relationship?' },
    { emoji: '🌟', text: 'What energy do we bring to each other?' },
    { emoji: '🌊', text: 'What is the purpose of this relationship?' },
    { emoji: '🦋', text: 'How can we grow together?' }
  ],
  es: [
    { emoji: '💕', text: '¿Cuál es la dinámica entre yo y esta persona?' },
    { emoji: '❤️', text: 'Ayúdame a entender mi relación con ellos' },
    { emoji: '🤝', text: '¿Qué necesita esta persona de mí?' },
    { emoji: '🌹', text: '¿Cuál es la lección en esta relación?' },
    { emoji: '💫', text: '¿Hacia dónde se dirige esta relación?' },
    { emoji: '🔮', text: '¿Cuál es la verdadera naturaleza de nuestra conexión?' },
    { emoji: '✨', text: '¿Cómo podemos mejorar nuestra relación?' },
    { emoji: '🌟', text: '¿Qué energía nos traemos el uno al otro?' },
    { emoji: '🌊', text: '¿Cuál es el propósito de esta relación?' },
    { emoji: '🦋', text: '¿Cómo podemos crecer juntos?' }
  ],
  it: [
    { emoji: '💕', text: 'Qual è la dinamica tra me e questa persona?' },
    { emoji: '❤️', text: 'Aiutami a capire la mia relazione con loro' },
    { emoji: '🤝', text: 'Di cosa ha bisogno questa persona da me?' },
    { emoji: '🌹', text: 'Qual è la lezione in questa relazione?' },
    { emoji: '💫', text: 'Dove sta andando questa relazione?' },
    { emoji: '🔮', text: 'Qual è la vera natura della nostra connessione?' },
    { emoji: '✨', text: 'Come possiamo migliorare la nostra relazione?' },
    { emoji: '🌟', text: 'Che energia ci portiamo l\'un l\'altro?' },
    { emoji: '🌊', text: 'Qual è lo scopo di questa relazione?' },
    { emoji: '🦋', text: 'Come possiamo crescere insieme?' }
  ]
};

// Fallback: General oracle card questions (for backward compatibility)
export const ORACLE_CARD_PLACEHOLDER_QUESTIONS = {
  en: [
    { emoji: '🌅', text: 'What energy should I embrace today?' },
    { emoji: '🔮', text: 'What do I need to know about my current path?' },
    { emoji: '✨', text: 'What guidance does the universe have for me?' },
    { emoji: '🌱', text: 'What is blocking my growth right now?' },
    { emoji: '💫', text: 'What am I being called to release?' },
    { emoji: '🌟', text: 'What opportunity is coming my way?' },
    { emoji: '🦋', text: 'What transformation is happening in my life?' },
    { emoji: '🌙', text: 'What do I need to see in the shadows?' },
    { emoji: '⚡', text: 'What action should I take next?' },
    { emoji: '🌊', text: 'How can I navigate this transition?' },
    { emoji: '🎯', text: 'What is my next step toward my goal?' },
    { emoji: '💎', text: 'What truth am I ready to face?' },
    { emoji: '🔥', text: 'What passion should I follow?' },
    { emoji: '🌿', text: 'How can I align with my highest self?' },
    { emoji: '🌈', text: 'What is the lesson in this situation?' },
    { emoji: '🕊️', text: 'How can I find peace in this challenge?' },
    { emoji: '🌌', text: 'What does my future hold?' },
    { emoji: '💭', text: 'What message does my intuition have for me?' },
    { emoji: '🌺', text: 'What beauty am I overlooking?' },
    { emoji: '🔑', text: 'What key will unlock my next chapter?' }
  ],
  es: [
    { emoji: '🌅', text: '¿Qué energía debo abrazar hoy?' },
    { emoji: '🔮', text: '¿Qué necesito saber sobre mi camino actual?' },
    { emoji: '✨', text: '¿Qué guía tiene el universo para mí?' },
    { emoji: '🌱', text: '¿Qué está bloqueando mi crecimiento ahora?' },
    { emoji: '💫', text: '¿Qué me está pidiendo que libere?' },
    { emoji: '🌟', text: '¿Qué oportunidad se acerca?' },
    { emoji: '🦋', text: '¿Qué transformación está ocurriendo en mi vida?' },
    { emoji: '🌙', text: '¿Qué necesito ver en las sombras?' },
    { emoji: '⚡', text: '¿Qué acción debo tomar a continuación?' },
    { emoji: '🌊', text: '¿Cómo puedo navegar esta transición?' },
    { emoji: '🎯', text: '¿Cuál es mi próximo paso hacia mi objetivo?' },
    { emoji: '💎', text: '¿Qué verdad estoy listo para enfrentar?' },
    { emoji: '🔥', text: '¿Qué pasión debo seguir?' },
    { emoji: '🌿', text: '¿Cómo puedo alinearme con mi mejor yo?' },
    { emoji: '🌈', text: '¿Cuál es la lección en esta situación?' },
    { emoji: '🕊️', text: '¿Cómo puedo encontrar paz en este desafío?' },
    { emoji: '🌌', text: '¿Qué me depara el futuro?' },
    { emoji: '💭', text: '¿Qué mensaje tiene mi intuición para mí?' },
    { emoji: '🌺', text: '¿Qué belleza estoy pasando por alto?' },
    { emoji: '🔑', text: '¿Qué llave abrirá mi próximo capítulo?' }
  ],
  it: [
    { emoji: '🌅', text: 'Quale energia dovrei abbracciare oggi?' },
    { emoji: '🔮', text: 'Cosa devo sapere sul mio percorso attuale?' },
    { emoji: '✨', text: 'Quale guida ha l\'universo per me?' },
    { emoji: '🌱', text: 'Cosa sta bloccando la mia crescita ora?' },
    { emoji: '💫', text: 'Cosa mi viene chiesto di rilasciare?' },
    { emoji: '🌟', text: 'Quale opportunità si sta avvicinando?' },
    { emoji: '🦋', text: 'Quale trasformazione sta avvenendo nella mia vita?' },
    { emoji: '🌙', text: 'Cosa devo vedere nelle ombre?' },
    { emoji: '⚡', text: 'Quale azione dovrei intraprendere dopo?' },
    { emoji: '🌊', text: 'Come posso navigare questa transizione?' },
    { emoji: '🎯', text: 'Qual è il mio prossimo passo verso il mio obiettivo?' },
    { emoji: '💎', text: 'Quale verità sono pronto ad affrontare?' },
    { emoji: '🔥', text: 'Quale passione dovrei seguire?' },
    { emoji: '🌿', text: 'Come posso allinearmi con il mio sé migliore?' },
    { emoji: '🌈', text: 'Qual è la lezione in questa situazione?' },
    { emoji: '🕊️', text: 'Come posso trovare pace in questa sfida?' },
    { emoji: '🌌', text: 'Cosa mi riserva il futuro?' },
    { emoji: '💭', text: 'Quale messaggio ha la mia intuizione per me?' },
    { emoji: '🌺', text: 'Quale bellezza sto trascurando?' },
    { emoji: '🔑', text: 'Quale chiave aprirà il mio prossimo capitolo?' }
  ]
};

export const DREAM_PLACEHOLDER_EXAMPLES = {
  en: [
    'I was flying over a vast ocean, feeling both exhilarated and a little scared...',
    'I was being chased through a dark forest by something I couldn\'t see...',
    'I found myself in a beautiful garden talking to someone I haven\'t seen in years...',
    'I was standing at the edge of a cliff, looking down at a city below...',
    'I was in my childhood home, but everything looked different and strange...',
    'I was swimming underwater, able to breathe and see clearly...',
    'I was lost in a maze, trying to find my way out...',
    'I was at a party where everyone was wearing masks...',
    'I was climbing a tall mountain, reaching for something at the top...',
    'I was in a library with endless shelves, searching for a specific book...'
  ],
  es: [
    'Estaba volando sobre un vasto océano, sintiéndome tanto emocionado como un poco asustado...',
    'Estaba siendo perseguido por un bosque oscuro por algo que no podía ver...',
    'Me encontré en un hermoso jardín hablando con alguien que no he visto en años...',
    'Estaba parado al borde de un acantilado, mirando hacia abajo a una ciudad...',
    'Estaba en la casa de mi infancia, pero todo se veía diferente y extraño...',
    'Estaba nadando bajo el agua, pudiendo respirar y ver claramente...',
    'Estaba perdido en un laberinto, tratando de encontrar la salida...',
    'Estaba en una fiesta donde todos llevaban máscaras...',
    'Estaba escalando una montaña alta, alcanzando algo en la cima...',
    'Estaba en una biblioteca con estantes infinitos, buscando un libro específico...'
  ],
  it: [
    'Stavo volando sopra un vasto oceano, sentendomi sia euforico che un po\' spaventato...',
    'Stavo venendo inseguito attraverso una foresta oscura da qualcosa che non riuscivo a vedere...',
    'Mi sono trovato in un bellissimo giardino a parlare con qualcuno che non vedo da anni...',
    'Ero in piedi sul bordo di una scogliera, guardando in basso verso una città...',
    'Ero nella casa della mia infanzia, ma tutto sembrava diverso e strano...',
    'Stavo nuotando sott\'acqua, riuscendo a respirare e vedere chiaramente...',
    'Ero perso in un labirinto, cercando di trovare l\'uscita...',
    'Ero a una festa dove tutti indossavano maschere...',
    'Stavo scalando una montagna alta, raggiungendo qualcosa in cima...',
    'Ero in una biblioteca con scaffali infiniti, cercando un libro specifico...'
  ]
};

export const DYNAMIC_PLACEHOLDER_QUESTIONS = {
  en: [
    { emoji: '💕', text: 'Will I find love this year?' },
    { emoji: '❤️', text: 'Will this relationship last?' },
    { emoji: '🎯', text: 'Should I change careers?' },
    { emoji: '📈', text: 'Is this opportunity right for me?' },
    { emoji: '💰', text: 'Is this investment wise?' },
    { emoji: '💸', text: 'Should I take the financial risk?' },
    { emoji: '✨', text: 'Am I making the right choice?' },
    { emoji: '🌱', text: 'Is this aligned with my values?' },
    { emoji: '👥', text: 'Should I end this friendship?' },
    { emoji: '🤝', text: 'How do I resolve this conflict?' }
  ],
  es: [
    { emoji: '💕', text: '¿Encontraré amor este año?' },
    { emoji: '❤️', text: '¿Durará esta relación?' },
    { emoji: '🎯', text: '¿Debería cambiar de carrera?' },
    { emoji: '📈', text: '¿Es esta oportunidad adecuada para mí?' },
    { emoji: '💰', text: '¿Es prudente esta inversión?' },
    { emoji: '💸', text: '¿Debería asumir el riesgo financiero?' },
    { emoji: '✨', text: '¿Estoy tomando la decisión correcta?' },
    { emoji: '🌱', text: '¿Está alineado con mis valores?' },
    { emoji: '👥', text: '¿Debería terminar esta amistad?' },
    { emoji: '🤝', text: '¿Cómo resuelvo este conflicto?' }
  ],
  it: [
    { emoji: '💕', text: "Troverò l'amore quest'anno?" },
    { emoji: '❤️', text: 'Durerà questa relazione?' },
    { emoji: '🎯', text: 'Dovrei cambiare carriera?' },
    { emoji: '📈', text: 'Questa opportunità è giusta per me?' },
    { emoji: '💰', text: 'È saggio questo investimento?' },
    { emoji: '💸', text: 'Dovrei correre il rischio finanziario?' },
    { emoji: '✨', text: 'Sto facendo la scelta giusta?' },
    { emoji: '🌱', text: 'È allineato con i miei valori?' },
    { emoji: '👥', text: 'Dovrei terminare questa amicizia?' },
    { emoji: '🤝', text: 'Come risolvo questo conflitto?' }
  ]
};

/**
 * Get ONE random question for placeholder.
 * @param {string} locale
 * @returns {{emoji: string, text: string}}
 */
export function getRandomPlaceholderQuestion(locale = 'en') {
  const questions = DYNAMIC_PLACEHOLDER_QUESTIONS[locale] || DYNAMIC_PLACEHOLDER_QUESTIONS.en;
  const randomIndex = Math.floor(Math.random() * questions.length);
  return questions[randomIndex];
}

/**
 * Format placeholder text with emoji and question.
 * @param {{emoji: string, text: string}|null} question
 * @returns {string}
 */
export function formatPlaceholder(question) {
  if (!question) {
    return 'Type your question...';
  }
  return `${question.emoji} "${question.text}"`;
}

/**
 * Get ONE random dream example for placeholder.
 * @param {string} locale
 * @returns {string}
 */
export function getRandomDreamPlaceholder(locale = 'en') {
  const examples = DREAM_PLACEHOLDER_EXAMPLES[locale] || DREAM_PLACEHOLDER_EXAMPLES.en;
  const randomIndex = Math.floor(Math.random() * examples.length);
  return examples[randomIndex];
}

/**
 * Get ONE random oracle card question for placeholder based on spread type.
 * Designed for oracle card readings with spread-specific questions.
 * @param {string} spreadType - The spread type: 'single-card', 'past-present-future', 'situation-action-outcome', 'five-card-cross', 'relationship'
 * @param {string} locale
 * @returns {{emoji: string, text: string}}
 */
export function getRandomOracleCardPlaceholder(spreadType = 'single-card', locale = 'en') {
  let questions;
  
  switch (spreadType) {
    case 'single-card':
      questions = SINGLE_CARD_PLACEHOLDER_QUESTIONS[locale] || SINGLE_CARD_PLACEHOLDER_QUESTIONS.en;
      break;
    case 'past-present-future':
      questions = PAST_PRESENT_FUTURE_PLACEHOLDER_QUESTIONS[locale] || PAST_PRESENT_FUTURE_PLACEHOLDER_QUESTIONS.en;
      break;
    case 'situation-action-outcome':
      questions = SITUATION_ACTION_OUTCOME_PLACEHOLDER_QUESTIONS[locale] || SITUATION_ACTION_OUTCOME_PLACEHOLDER_QUESTIONS.en;
      break;
    case 'five-card-cross':
      questions = FIVE_CARD_CROSS_PLACEHOLDER_QUESTIONS[locale] || FIVE_CARD_CROSS_PLACEHOLDER_QUESTIONS.en;
      break;
    case 'relationship':
      questions = RELATIONSHIP_SPREAD_PLACEHOLDER_QUESTIONS[locale] || RELATIONSHIP_SPREAD_PLACEHOLDER_QUESTIONS.en;
      break;
    default:
      // Fallback to general oracle card questions
      questions = ORACLE_CARD_PLACEHOLDER_QUESTIONS[locale] || ORACLE_CARD_PLACEHOLDER_QUESTIONS.en;
  }
  
  const randomIndex = Math.floor(Math.random() * questions.length);
  return questions[randomIndex];
}

/**
 * Format oracle card placeholder text with emoji and question.
 * @param {{emoji: string, text: string}|null} question
 * @returns {string}
 */
export function formatOracleCardPlaceholder(question) {
  if (!question) {
    return 'Enter your question or intention...';
  }
  return `${question.emoji} "${question.text}"`;
}

